<template>
  <div class="chart-clustered-column" ref="chartdiv" :style="{ height: chartSettings.chartHeight ? chartSettings.chartHeight + 'px' : '400px' }"></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import * as _ from 'underscore'
import { mapGetters } from 'vuex'

export default {
  name: 'chartClusteredColumnRender',
  props: {
    chartKey: { type: String, required: true },
    fromDialog: { type: Boolean, required: false }
  },
  data () {
    return {
      chartSettings: {},
      chartData: []
    }
  },
  computed: {
    ...mapGetters(['translation']),
    chartObj: {
      get () { return this.$store.getters.getChartByKey(this.chartKey) }
    }
  },
  mounted () {
    let self = this
    this.chartSettings = this.chartObj.chartSettings
    this.chartData = this.chartObj.data.result

    // check and reduce data if not in dialog
    if (!this.fromDialog && this.chartData.length > (this.chartObj.chartSettings.displayLength || 10)) {
      const reducedData = this.chartData.filter((data, index) => index < (this.chartObj.chartSettings.displayLength || 10))
      this.chartData = reducedData
    }

    let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)

    // Add data
    chart.data = this.chartData

    if (this.chartSettings.legend) {
      chart.legend = new am4charts.Legend()
      chart.legend.position = this.chartSettings.legendPosition || 'bottom'
      chart.legend.paddingBottom = 20
      chart.legend.labels.template.maxWidth = 95
      chart.legend.useDefaultMarker = true
      let marker = chart.legend.markers.template.children.getIndex(0)
      marker.cornerRadius(12, 12, 12, 12)
    }

    let xAxis = chart.xAxes.push(new am4charts.CategoryAxis())
    xAxis.dataFields.category = 'name'
    xAxis.renderer.cellStartLocation = 0.1
    xAxis.renderer.cellEndLocation = 0.9
    xAxis.renderer.grid.template.location = 0
    xAxis.renderer.minGridDistance = self.chartSettings.gridDistance || 25

    let yAxis = chart.yAxes.push(new am4charts.ValueAxis())
    yAxis.min = 0

    function createSeries (value, name) {
      let series = chart.series.push(new am4charts.ColumnSeries())
      series.dataFields.valueY = value
      series.dataFields.categoryX = 'name'
      series.name = name

      series.events.on('hidden', arrangeColumns)
      series.events.on('shown', arrangeColumns)

      if (self.chartSettings.tooltipText) {
        let tooltipText = self.chartSettings.tooltipText
        series.columns.template.tooltipText =
          `${tooltipText[0]}: [bold]{name}[/]\n ${tooltipText[1]}: [bold]${name}[/]\n ${tooltipText[2]}: [bold]{valueY}[/]`
      } else {
        series.columns.template.tooltipText = `${name}: [bold]{valueY}[/]`
      }

      series.tooltip.getFillFromObject = false
      series.tooltip.background.fill = am4core.color('#383336')
      series.tooltip.background.stroke = am4core.color('#383336')
      series.tooltip.pointerOrientation = 'right'
      series.tooltip.label.fill = am4core.color('#e0dbde')
      series.tooltip.label.textAlign = 'middle'
      series.tooltip.label.paddingBottom = '10'
      series.tooltip.label.paddingTop = '10'

      // let bullet = series.bullets.push(new am4charts.LabelBullet())
      // bullet.interactionsEnabled = false
      // bullet.dy = 30
      // bullet.label.text = '{valueY}'
      // bullet.label.fill = am4core.color('#ffffff')

      // Category labels
      let categoryAxisLabel = xAxis.renderer.labels.template
      categoryAxisLabel.rotation = self.chartSettings.labelRotation || 0
      categoryAxisLabel.horizontalCenter = self.chartSettings.labelHorizontalCenter || 'middle'
      categoryAxisLabel.verticalCenter = self.chartSettings.labelVerticalCenter || 'middle'
      categoryAxisLabel.textAlign = self.chartSettings.textAlign || null
      self.chartSettings.truncate !== undefined
        ? categoryAxisLabel.truncate = self.chartSettings.truncate
        : categoryAxisLabel.truncate = true

      if (categoryAxisLabel.truncate) {
        categoryAxisLabel.tooltipText = '{category}'
      }

      if (self.chartSettings.maxLabelWidth) {
        categoryAxisLabel.maxWidth = self.chartSettings.maxLabelWidth
      }

      return series
    }

    let dataKeys = _.keys(this.chartData[0])
    _.each(dataKeys, function (key) {
      // skip name since it's category data
      if (key === 'name') {
        return
      }
      if (self.chartSettings.translation) {
        createSeries(key, self.translation(`chart.data.value.${key}`))
      } else {
        createSeries(key, key)
      }
    })

    function arrangeColumns () {
      let series = chart.series.getIndex(0)

      let w = 1 - xAxis.renderer.cellStartLocation - (1 - xAxis.renderer.cellEndLocation)
      if (series.dataItems.length > 1) {
        let x0 = xAxis.getX(series.dataItems.getIndex(0), 'categoryX')
        let x1 = xAxis.getX(series.dataItems.getIndex(1), 'categoryX')
        let delta = ((x1 - x0) / chart.series.length) * w
        if (am4core.isNumber(delta)) {
          let middle = chart.series.length / 2
          let newIndex = 0
          chart.series.each(function (series) {
            if (!series.isHidden && !series.isHiding) {
              series.dummyData = newIndex
              newIndex++
            } else {
              series.dummyData = chart.series.indexOf(series)
            }
          })
          let visibleCount = newIndex
          let newMiddle = visibleCount / 2

          chart.series.each(function (series) {
            let trueIndex = chart.series.indexOf(series)
            let newIndex = series.dummyData

            let dx = (newIndex - trueIndex + middle - newMiddle) * delta

            series.animate({ property: 'dx', to: dx }, series.interpolationDuration, series.interpolationEasing)
            series.bulletsContainer.animate({ property: 'dx', to: dx }, series.interpolationDuration, series.interpolationEasing)
          })
        }
      }
    }

    this.chart = chart
  },
  methods: {},
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .chart-clustered-column {
    width: 100%;
    height: 400px;
  }
</style>
